import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  useFormField,
  Form,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
  FormField,
} from '@/components/ui/form';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@headlessui/react';
import { ChevronLeft } from 'lucide-react';

const formSchema = z.object({
  rfq_number: z.string().min(1, 'Required'),
  po_date: z.string().min(1, 'Required'),
  shipping_method: z.string().min(1, 'Required'),
  promised_date: z.string().min(1, 'Required'),
  bill_company_name: z.string().min(1, 'Required'),
  bill_contact_person: z.string().min(1, 'Required'),
  bill_address: z.string().min(1, 'Required'),
  bill_phone: z.string().min(1, 'Required'),
  bill_email: z.string().email('Invalid email').min(1, 'Required'),
  vendor_company_name: z.string().min(1, 'Required'),
  vendor_contact_person: z.string().min(1, 'Required'),
  vendor_address: z.string().min(1, 'Required'),
  vendor_phone: z.string().min(1, 'Required'),
  vendor_email: z.string().email('Invalid email').min(1, 'Required'),
  ship_company_name: z.string().min(1, 'Required'),
  ship_contact_person: z.string().min(1, 'Required'),
  ship_delivery_address: z.string().min(1, 'Required'),
  ship_phone: z.string().min(1, 'Required'),
  ship_email: z.string().email('Invalid email').min(1, 'Required'),
  part_number: z.string().min(1, 'Required'),
  part_name: z.string().min(1, 'Required'),
  quantity: z.number().min(1, 'Required'),
  unit_price: z.number().min(0, 'Required'),
  manufacturer_note: z.string().optional(),
  discount: z.string().optional(),
  import_duty: z.string().optional(),
  tax: z.string().optional(),
});

export default function CreatePOScreen({ setScreen, PO, setPO, setField }) {
  const form = useForm({
    resolver: zodResolver(formSchema),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: {
      rfq_number: '',
      po_date: '123',
      shipping_method: '',
      promised_date: '',
      bill_company_name: '',
      bill_contact_person: '',
      bill_address: '',
      bill_phone: '',
      bill_email: '',
      vendor_company_name: '',
      vendor_contact_person: '',
      vendor_address: '',
      vendor_phone: '',
      vendor_email: '',
      ship_company_name: '',
      ship_contact_person: '',
      ship_delivery_address: '',
      ship_phone: '',
      ship_email: '',
      part_number: '123',
      part_name: 'xyz',
      quantity: '2',
      unit_price: '50',
      manufacturer_note: '',
      discount: '',
      import_duty: '',
      tax: '',
    },
  });

  const onSubmit = (data) => {
    console.log(data);
  };

  const watchedValues = form.watch();
  const calculateTotalPrice = () => {
    const quantity = watchedValues.quantity || 0;
    const unitPrice = watchedValues.unit_price || 0;
    return (quantity * unitPrice).toFixed(2);
  };

  const DocumentCard = ({ fileName, fileSize, onDelete }) => {
    return (
      <div className="border border-gray-200 rounded-lg p-4 bg-white flex items-center space-x-3 max-w-xs">
        <div className="w-16 h-16 bg-gray-100 rounded flex items-center justify-center flex-shrink-0">
          <div className="w-8 h-8 bg-gray-300 opacity-50"></div>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 mb-1">{fileName}</p>
          <p className="text-xs text-gray-400">{fileSize}</p>
        </div>
        <button
          onClick={onDelete}
          className="text-gray-400 hover:text-red-500 p-1 flex-shrink-0"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            ></path>
          </svg>
        </button>
      </div>
    );
  };

  return (
    <div className="py-8 min-h-screen">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex gap-4 items-center mb-6">
            <ChevronLeft
              className="h-5 w-5 hover:cursor-pointer"
              onClick={() => {
                setField('Purchase_Order');
                setScreen('ALL_PARTS');
              }}
            />
            <h1 className="text-lg font-medium">
              Create PO (Contract #{PO.contract})
            </h1>
          </div>
          <div className="flex items-center space-x-4">
            <Button
              className="bg-darkPurple hover:bg-darkerPurple text-white"
              onClick={console.log('clicked')}
            >
              Send to Team for Approval
            </Button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-6">
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                onKeyDown={(e) => {
                  if (
                    e.key === 'Enter' &&
                    e.target.tagName === 'INPUT' &&
                    e.target.tagName !== 'TEXTAREA' &&
                    !e.target.getAttribute('data-allow-enter')
                  ) {
                    e.preventDefault();
                  }
                }}
                className="space-y-4"
              >
                <div className="space-y-8">
                  <div className="grid grid-cols-2 gap-8">
                    <div className=" bg-gray-50 p-4 rounded-lg w-full">
                      <div className="mb-6">
                        <div className="text-xl font-semibold w-64 flex justify-between text-gray-700 mb-1">
                          <div>P.O. Number:</div>
                          <div>#1</div>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <FormField
                          control={form.control}
                          name="rfq_number"
                          render={({ field, fieldState }) => (
                            <FormItem className="flex w-full items-start">
                              <FormLabel className="w-32 pt-2 text-sm font-medium text-gray-700">
                                RFQ Number
                              </FormLabel>
                              <FormControl className="flex-1">
                                <Input
                                  {...field}
                                  placeholder="RFQ Number"
                                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="your_field_name"
                          render={({ field }) => (
                            <FormItem className="flex items-start">
                              <FormLabel className="w-32 pt-2 text-sm font-medium text-gray-700">
                                P.O. Date
                              </FormLabel>
                              <div className="flex-1">
                                <FormControl>
                                  <Select
                                    onValueChange={field.onChange}
                                    value={field.value}
                                  >
                                    <SelectTrigger className="w-full focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple group-hover:border-lightPurple">
                                      {field.value || (
                                        <span className="text-slate-500">
                                          Select material category
                                        </span>
                                      )}
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem
                                        value="pcs"
                                        className="focus:bg-veryLightPurple focus:bg-opacity-15"
                                      >
                                        Pieces
                                      </SelectItem>
                                      <SelectItem
                                        value="sets"
                                        className="focus:bg-veryLightPurple focus:bg-opacity-15"
                                      >
                                        Sets
                                      </SelectItem>
                                      <SelectItem
                                        value="units"
                                        className="focus:bg-veryLightPurple focus:bg-opacity-15"
                                      >
                                        Units
                                      </SelectItem>
                                    </SelectContent>
                                  </Select>
                                </FormControl>
                                <FormMessage />
                              </div>
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="your_field_name"
                          render={({ field }) => (
                            <FormItem className="flex items-start">
                              <FormLabel className="w-32 pt-2 text-sm font-medium text-gray-700">
                                Shipping Method
                              </FormLabel>
                              <div className="flex-1">
                                <FormControl>
                                  <Select
                                    onValueChange={field.onChange}
                                    value={field.value}
                                  >
                                    <SelectTrigger className="w-full focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple group-hover:border-lightPurple">
                                      {field.value || (
                                        <span className="text-slate-500">
                                          Select material category
                                        </span>
                                      )}
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem
                                        value="pcs"
                                        className="focus:bg-veryLightPurple focus:bg-opacity-15"
                                      >
                                        Pieces
                                      </SelectItem>
                                      <SelectItem
                                        value="sets"
                                        className="focus:bg-veryLightPurple focus:bg-opacity-15"
                                      >
                                        Sets
                                      </SelectItem>
                                      <SelectItem
                                        value="units"
                                        className="focus:bg-veryLightPurple focus:bg-opacity-15"
                                      >
                                        Units
                                      </SelectItem>
                                    </SelectContent>
                                  </Select>
                                </FormControl>
                                <FormMessage />
                              </div>
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="your_field_name"
                          render={({ field }) => (
                            <FormItem className="flex items-start">
                              <FormLabel className="w-32 pt-2 text-sm font-medium text-gray-700">
                                Promised Date
                              </FormLabel>
                              <div className="flex-1">
                                <FormControl>
                                  <Select
                                    onValueChange={field.onChange}
                                    value={field.value}
                                  >
                                    <SelectTrigger className="w-full focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple group-hover:border-lightPurple">
                                      {field.value || (
                                        <span className="text-slate-500">
                                          Select material category
                                        </span>
                                      )}
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem
                                        value="pcs"
                                        className="focus:bg-veryLightPurple focus:bg-opacity-15"
                                      >
                                        Pieces
                                      </SelectItem>
                                      <SelectItem
                                        value="sets"
                                        className="focus:bg-veryLightPurple focus:bg-opacity-15"
                                      >
                                        Sets
                                      </SelectItem>
                                      <SelectItem
                                        value="units"
                                        className="focus:bg-veryLightPurple focus:bg-opacity-15"
                                      >
                                        Units
                                      </SelectItem>
                                    </SelectContent>
                                  </Select>
                                </FormControl>
                                <FormMessage />
                              </div>
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="text-base font-medium bg-gray-100 p-1 text-darkerPurple mb-4">
                        Bill To
                      </h3>
                      <div className="space-y-4">
                        <FormField
                          control={form.control}
                          name="bill_company_name"
                          render={({ field, fieldState }) => (
                            <FormItem className="flex w-full gap-4 items-start">
                              <FormLabel className="w-40 pt-2 text-sm font-medium text-gray-700">
                                Company Name
                              </FormLabel>
                              <FormControl className="flex-1">
                                <Input
                                  {...field}
                                  placeholder="Company Name"
                                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="bill_contact_person"
                          render={({ field, fieldState }) => (
                            <FormItem className="flex w-full gap-4 items-start">
                              <FormLabel className="w-40 pt-2 text-sm font-medium text-gray-700">
                                Contact Person
                              </FormLabel>
                              <FormControl className="flex-1">
                                <Input
                                  {...field}
                                  placeholder="Contact Person"
                                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="bill_address"
                          render={({ field, fieldState }) => (
                            <FormItem className="flex w-full gap-4 items-start">
                              <FormLabel className="w-40 pt-2 text-sm font-medium text-gray-700">
                                Address
                              </FormLabel>
                              <FormControl className="flex-1">
                                <Input
                                  {...field}
                                  placeholder="Address"
                                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="bill_phone"
                          render={({ field, fieldState }) => (
                            <FormItem className="flex w-full gap-4 items-start">
                              <FormLabel className="w-40 pt-2 text-sm font-medium text-gray-700">
                                Phone
                              </FormLabel>
                              <FormControl className="flex-1">
                                <Input
                                  {...field}
                                  placeholder="Phone"
                                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="bill_email"
                          render={({ field, fieldState }) => (
                            <FormItem className="flex w-full gap-4 items-start">
                              <FormLabel className="w-40 pt-2 text-sm font-medium text-gray-700">
                                Email Address
                              </FormLabel>
                              <FormControl className="flex-1">
                                <Input
                                  {...field}
                                  type="email"
                                  placeholder="Email Address"
                                  className="w-full px-4 py-2 border border-gray-300 rounded-mdfocus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-8">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="text-base font-semibold bg-gray-100 p-1 text-darkPurple mb-4">
                        Vendor
                      </h3>
                      <div className="space-y-4">
                        <FormField
                          control={form.control}
                          name="vendor_company_name"
                          render={({ field, fieldState }) => (
                            <FormItem className="flex items-start">
                              <FormLabel className="w-32 pt-2 text-sm font-medium text-gray-700">
                                Company Name
                              </FormLabel>
                              <FormControl className="flex-1">
                                <Input
                                  {...field}
                                  placeholder="Company Name"
                                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="vendor_contact_person"
                          render={({ field, fieldState }) => (
                            <FormItem className="flex items-start">
                              <FormLabel className="w-32 pt-2 text-sm font-medium text-gray-700">
                                Contact Person
                              </FormLabel>
                              <FormControl className="flex-1">
                                <Input
                                  {...field}
                                  placeholder="Contact Person"
                                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="vendor_address"
                          render={({ field, fieldState }) => (
                            <FormItem className="flex items-start">
                              <FormLabel className="w-32 pt-2 text-sm font-medium text-gray-700">
                                Address
                              </FormLabel>
                              <FormControl className="flex-1">
                                <Input
                                  {...field}
                                  placeholder="Address"
                                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="vendor_phone"
                          render={({ field, fieldState }) => (
                            <FormItem className="flex items-start">
                              <FormLabel className="w-32 pt-2 text-sm font-medium text-gray-700">
                                Phone
                              </FormLabel>
                              <FormControl className="flex-1">
                                <Input
                                  {...field}
                                  placeholder="Phone"
                                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="vendor_email"
                          render={({ field, fieldState }) => (
                            <FormItem className="flex items-start">
                              <FormLabel className="w-32 pt-2 text-sm font-medium text-gray-700">
                                Email Address
                              </FormLabel>
                              <div className="flex-1">
                                <Input
                                  {...field}
                                  type="email"
                                  placeholder="Email Address"
                                  className="w-full px-4 py-2 border border-gray-300 rounded-mdfocus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                                />
                              </div>
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="text-base font-semibold bg-gray-100 p-1 text-darkPurple mb-4">
                        Ship To
                      </h3>
                      <div className="space-y-4">
                        <FormField
                          control={form.control}
                          name="ship_company_name"
                          render={({ field, fieldState }) => (
                            <FormItem className="flex items-start">
                              <FormLabel className="w-32 pt-2 text-sm font-medium text-gray-700">
                                Company Name
                              </FormLabel>
                              <FormControl className="flex-1">
                                <Input
                                  {...field}
                                  placeholder="Company Name"
                                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="ship_contact_person"
                          render={({ field, fieldState }) => (
                            <FormItem className="flex items-start">
                              <FormLabel className="w-32 pt-2 text-sm font-medium text-gray-700">
                                Contact Person
                              </FormLabel>
                              <FormControl className="flex-1">
                                <Input
                                  {...field}
                                  placeholder="Contact Person"
                                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="ship_delivery_address"
                          render={({ field, fieldState }) => (
                            <FormItem className="flex items-start">
                              <FormLabel className="w-32 pt-2 text-sm font-medium text-gray-700">
                                Delivery Address
                              </FormLabel>
                              <FormControl className="flex-1">
                                <Input
                                  {...field}
                                  placeholder="Delivery Address"
                                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="ship_phone"
                          render={({ field, fieldState }) => (
                            <div className="flex items-start">
                              <FormLabel className="w-32 pt-2 text-sm font-medium text-gray-700">
                                Phone
                              </FormLabel>
                              <div className="flex-1">
                                <Input
                                  {...field}
                                  placeholder="Phone"
                                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                                />
                              </div>
                            </div>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="ship_email"
                          render={({ field, fieldState }) => (
                            <FormItem className="flex items-start">
                              <FormLabel className="w-32 pt-2 text-sm font-medium text-gray-700">
                                Email Address
                              </FormLabel>
                              <FormControl className="flex-1">
                                <Input
                                  {...field}
                                  type="email"
                                  placeholder="Email Address"
                                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4">
                    <h3 className="text-base font-semibold text-darkPurple bg-gray-100 p-1 mb-4">
                      Order Details
                    </h3>
                    <div className="overflow-hidden">
                      <div className="bg-veryVeryLightPurple grid grid-cols-5 gap-4 p-2 font-medium text-sm text-center font-semibold text-black mb-4">
                        <div>Part Number</div>
                        <div>Part Name</div>
                        <div>Quantity</div>
                        <div>Unit Price</div>
                        <div>Total Price</div>
                      </div>
                      <div className="grid grid-cols-5 gap-4 p-4 text-center bg-white">
                        <div className="text-darkerPurple font-semibold">
                          {watchedValues.part_number}
                        </div>
                        <div>{watchedValues.part_name}</div>
                        <div>{watchedValues.quantity}</div>
                        <div>${watchedValues.unit_price}</div>
                        <div>${calculateTotalPrice()}</div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 ">
                    <h3 className="text-base font-semibold text-darkPurple bg-gray-100 p-1 mb-4">
                      Note for Manufacturer
                    </h3>
                    <FormField
                      control={form.control}
                      name="manufacturer_note"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormControl>
                            <Textarea
                              {...field}
                              rows={4}
                              placeholder="Some note for manufacturer "
                              className="w-full border rounded-lg p-1 focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                            />
                          </FormControl>
                          <FormMessage />
                          <FormMessage>{fieldState.error?.message}</FormMessage>
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="bg-gray-100 p-1 mb-6 text-darkPurple font-semibold">
                      Order Summary
                    </div>
                    <FormField
                      control={form.control}
                      name="ship_contact_person"
                      render={({ field, fieldState }) => (
                        <FormItem className="bg-veryVeryLightPurple mb-4 flex items-center">
                          <FormLabel className="text-sm font-medium text-gray-500 w-1/2 text-center"></FormLabel>
                          <FormControl className="flex-1 pr-4">
                            <Input
                              value="Amount"
                              readOnly
                              className="border-none bg-transparent font-semibold shadow-none focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple p-0 text-center"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <div className="space-y-4">
                      <FormField
                        control={form.control}
                        name="ship_contact_person"
                        render={({ field, fieldState }) => (
                          <FormItem className="bg-white p-2 flex items-center">
                            <FormLabel className="text-sm font-medium text-gray-500 w-1/2 text-center">
                              Subtotal
                            </FormLabel>
                            <FormControl className="flex-1 pr-4">
                              <Input
                                value="100000"
                                readOnly
                                type="number"
                                className="border-none bg-transparent shadow-none focus:ring-0 focus:outline-none focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple p-0 text-center"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="discount"
                        render={({ field, fieldState }) => (
                          <FormItem className="bg-white p-2 flex items-center">
                            <FormLabel className="text-sm font-medium text-gray-500 w-1/2 text-center">
                              Discount (if any)
                            </FormLabel>
                            <FormControl className="flex-1 pr-4">
                              <Input
                                {...field}
                                placeholder="Enter Discount Percentage"
                                className="w-full focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="import_duty"
                        render={({ field, fieldState }) => (
                          <FormItem className="bg-white p-2 flex items-center">
                            <FormLabel className="text-sm font-medium text-gray-500 w-1/2 text-center">
                              Import Duty
                            </FormLabel>
                            <FormControl className="flex-1 pr-4">
                              <Input
                                {...field}
                                placeholder="Enter Import Duty"
                                className="w-full focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="tax"
                        render={({ field, fieldState }) => (
                          <FormItem className="bg-white p-2 flex items-center">
                            <FormLabel className="text-sm font-medium text-gray-500 w-1/2 text-center">
                              Tax (e.g. 12%)
                            </FormLabel>
                            <FormControl className="flex-1 pr-4">
                              <Input
                                {...field}
                                placeholder="Enter Tax"
                                className="w-full focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="ship_contact_person"
                        render={({ field, fieldState }) => (
                          <FormItem className="bg-gray-100 p-2 flex items-center">
                            <FormLabel className="text-sm font-bold text-black w-1/2 text-center">
                              Total Cost
                            </FormLabel>
                            <FormControl className="flex-1 pr-4">
                              <Input
                                value="100000"
                                readOnly
                                type="number"
                                className="border-none bg-transparent shadow-none focus:ring-0 text-center focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple p-0"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h2 className="text-darkPurple bg-gray-100 font-semibold mb-4">
                      Confirmed Contract Document
                    </h2>

                    <DocumentCard fileName="Document" fileSize="10 MB" />
                  </div>
                </div>
              </form>
            </Form>
          </div>
          <div className="flex justify-center items-center space-x-4">
            <Button
              className="bg-darkPurple hover:bg-darkerPurple text-white mb-4"
              type="submit"
            >
              Send to Team for Approval
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
